/**
 * Section 09d — CLN_Break Strategy.
 *
 * The rule lives in utils/strategies/clnBreakEngine.ts, a byte-identical twin of
 * backend/services/strategies/clnBreakEngine.ts. Every number on this page is
 * recomputed by `cd backend && npm run verify:cln-break` — if one changes there
 * it has to change here, which is the point of writing them down.
 */
import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_strategy_clnbreak: GuideSection = {
  id: 'strategy-clnbreak',
  label: 'CLN_Break Strategy',
  group: 'strategies',
  icon: <TrendingUpIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>CLN_Break Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A stop-and-reverse system on the <strong>linear-regression bodies</strong> of the CLN candle. Two levels on
        every bar; price touching one takes that side and squares off the other.
      </Typography>

      <GuideCallout kind="warning" title="Chart marks only — and it did not pass its own test">
        There is no scan, no alert and no auto-trade behind this strategy: it draws ▲ B where it would buy, ▼ S where
        it would short, and that is all. It is on the chart so you can look at it, <strong>not</strong> because it was
        shown to work — it was measured against a coin with the same turnover and against buy-and-hold, and it did
        not clear either. The numbers are below, in full.
      </GuideCallout>

      <H id="clnbreak-rule">The rule, in full</H>
      <P>
        Turn on <strong>CLN</strong> candles and you see two layers: the real market candle, and a wickless
        linear-regression body drawn over it. This strategy trades the <em>regression</em> body, never the real one.
      </P>
      <P>
        For every bar, <strong>the long level</strong> is the highest of the previous three regression body tops and
        <strong> the short level</strong> is the lowest of their bottoms. Price trading through the long level takes
        the long side; through the short level, the short side. Whichever side you were on is squared off at the same
        price, so the system is <strong>always in the market</strong> and has no stop of its own.
      </P>
      <P>
        The bodies are <strong>the ones on your chart</strong>: the rule uses the CLN length set under{' '}
        <strong>Chart Settings → Candle</strong>, and has no length of its own. So every ▲ B sits on a bar whose real
        high reached the top of the three bodies before it, and every ▼ S on a bar whose real low reached their
        bottom — bodies you can see.
      </P>
      <P>
        Levels come from bars <em>before</em> the one being traded — the current bar&rsquo;s own regression body is
        not known until it closes, so using it would be reading the future. At the default length of 14, the first 17
        bars of any chart carry no level at all: fourteen for the regression, fourteen for the ATR, and three more for
        the level window. A longer regression pushes that out to its own length plus three.
      </P>

      <H id="clnbreak-narrow">The one thing to understand before using it</H>
      <P>
        A regression body is <strong>much smaller than a real candle</strong>. That has a consequence which decides
        everything else about this strategy: the channel between the two levels is{' '}
        <strong>narrower than one average bar&rsquo;s true range</strong> — 0.72×ATR on crypto, 0.78×ATR on tokenised
        stocks and on equities, measured over about 650,000 bars.
      </P>

      <Box sx={{ my: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sample</TableCell>
              <TableCell align="right">Channel width</TableCell>
              <TableCell align="right">Bars breaching a level</TableCell>
              <TableCell align="right">Bars breaching <em>both</em></TableCell>
              <TableCell align="right">Flips every</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>Crypto perps, daily</TableCell><TableCell align="right">0.72 × ATR</TableCell><TableCell align="right">96.1%</TableCell><TableCell align="right">13.4%</TableCell><TableCell align="right">4.3 bars</TableCell></TableRow>
            <TableRow><TableCell>RWA perps, daily</TableCell><TableCell align="right">0.78 × ATR</TableCell><TableCell align="right">95.0%</TableCell><TableCell align="right">14.4%</TableCell><TableCell align="right">4.1 bars</TableCell></TableRow>
            <TableRow><TableCell>Equities, daily</TableCell><TableCell align="right">0.78 × ATR</TableCell><TableCell align="right">96.0%</TableCell><TableCell align="right">9.7%</TableCell><TableCell align="right">4.6 bars</TableCell></TableRow>
            <TableRow><TableCell>Crypto perps, 8H</TableCell><TableCell align="right">0.72 × ATR</TableCell><TableCell align="right">—</TableCell><TableCell align="right">16.9%</TableCell><TableCell align="right">3.8 bars</TableCell></TableRow>
            <TableRow><TableCell>Crypto perps, 4H</TableCell><TableCell align="right">0.72 × ATR</TableCell><TableCell align="right">—</TableCell><TableCell align="right">17.8%</TableCell><TableCell align="right">3.7 bars</TableCell></TableRow>
          </TableBody>
        </Table>
      </Box>

      <P>
        Read the last two columns together. At least one level is breached on essentially <strong>every</strong> bar,
        and <strong>both</strong> are breached on roughly one bar in seven. That is not an edge case — it is the
        single biggest driver of how this strategy behaves.
      </P>

      <GuideCallout kind="info" title="What happens when both levels are hit on the same bar">
        The bar could have gone either way and nobody can tell from daily data which level was touched first, so the
        strategy assumes <strong>the worst</strong>: the side <em>against</em> your open position is taken to have
        filled first. Long and both levels hit → you end that bar short. One reversal per bar, never two.
        <br /><br />
        This is the honest assumption, and it is also expensive: every ambiguous bar becomes a reversal. That is why
        the rule flips every 4.3 bars rather than every 5.9 — the tie-break alone adds about a third to the turnover.
      </GuideCallout>

      <H id="clnbreak-cost">Why the timeframe decides the outcome</H>
      <P>
        The channel width is the <strong>same at every timeframe</strong> — 0.72×ATR on 4H, 8H and daily crypto
        alike. The flip <em>rate</em> is close to constant too. So the number of round trips a year is set almost
        entirely by how many bars a year the timeframe has, and cost scales straight with it. At the house figure of
        0.20% a round trip, on a 24×7 venue:
      </P>
      <Box sx={{ my: 2 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Timeframe</TableCell><TableCell align="right">Round trips / year</TableCell><TableCell align="right">Cost drag</TableCell></TableRow></TableHead>
          <TableBody>
            <TableRow><TableCell>Daily</TableCell><TableCell align="right">≈ 85</TableCell><TableCell align="right">≈ 17% a year</TableCell></TableRow>
            <TableRow><TableCell>8 hour</TableCell><TableCell align="right">≈ 290</TableCell><TableCell align="right">≈ 58% a year</TableCell></TableRow>
            <TableRow><TableCell>4 hour</TableCell><TableCell align="right">≈ 595</TableCell><TableCell align="right">≈ 119% a year</TableCell></TableRow>
          </TableBody>
        </Table>
      </Box>
      <P>
        A 4-hour chart starts <strong>119% a year behind</strong> before the rule has been right about anything. That
        was written down as a prediction before the backtest ran, and the backtest agreed with it.
      </P>

      <H id="clnbreak-measured">What it actually did</H>
      <P>
        Backtested on 2026-09-22 over seven samples — a ten-year equity set, Delta crypto perpetuals at daily, 8-hour
        and 4-hour, and the RWA perpetuals at all three — against a pre-registered protocol with the pass mark fixed
        before the run. Every figure is the <strong>median across symbols of the compound annual return, net of 0.20%
        a trade</strong>.
      </P>
      <GuideCallout kind="warning" title="Measured at CLN length 14 only">
        Every return below was measured with a regression length of 14. Since 2026-09-24 the levels follow the
        chart&rsquo;s own CLN length, so the marks always refer to the bodies on screen — but on a chart drawn at any
        other length (50, say) these figures do not describe what you are looking at. That has not been re-measured.
      </GuideCallout>
      <Box sx={{ my: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sample</TableCell>
              <TableCell align="right">Net / year</TableCell>
              <TableCell align="right">Gross / year</TableCell>
              <TableCell align="right">Buy &amp; hold</TableCell>
              <TableCell align="right">Beat a coin with the same turnover?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>Equities, 10 years, daily</TableCell><TableCell align="right"><strong>−9.1%</strong></TableCell><TableCell align="right">+1.3%</TableCell><TableCell align="right">+27.9%</TableCell><TableCell align="right">yes</TableCell></TableRow>
            <TableRow><TableCell>Crypto perps, daily</TableCell><TableCell align="right"><strong>−22.3%</strong></TableCell><TableCell align="right">−7.2%</TableCell><TableCell align="right">−68.2%</TableCell><TableCell align="right">yes</TableCell></TableRow>
            <TableRow><TableCell>Crypto perps, 8H</TableCell><TableCell align="right"><strong>−59.4%</strong></TableCell><TableCell align="right">−25.5%</TableCell><TableCell align="right">−25.5%</TableCell><TableCell align="right">yes</TableCell></TableRow>
            <TableRow><TableCell>Crypto perps, 4H</TableCell><TableCell align="right"><strong>−84.2%</strong></TableCell><TableCell align="right">−47.6%</TableCell><TableCell align="right">−25.7%</TableCell><TableCell align="right"><strong>no</strong></TableCell></TableRow>
            <TableRow><TableCell>RWA perps, daily</TableCell><TableCell align="right"><strong>−22.7%</strong></TableCell><TableCell align="right">−5.6%</TableCell><TableCell align="right">+32.2%</TableCell><TableCell align="right"><strong>no</strong></TableCell></TableRow>
            <TableRow><TableCell>RWA perps, 8H</TableCell><TableCell align="right"><strong>+12.4%</strong></TableCell><TableCell align="right">+80.8%</TableCell><TableCell align="right">+37.1%</TableCell><TableCell align="right">yes</TableCell></TableRow>
            <TableRow><TableCell>RWA perps, 4H</TableCell><TableCell align="right"><strong>−16.7%</strong></TableCell><TableCell align="right">+119.9%</TableCell><TableCell align="right">+33.7%</TableCell><TableCell align="right">yes</TableCell></TableRow>
          </TableBody>
        </Table>
      </Box>

      <GuideCallout kind="warning" title="The levels do carry information. It is nowhere near enough to pay for the turnover.">
        The rule beat a coin flipping at the same rate in <strong>5 of the 7 samples</strong> — so the levels are not
        noise. But it beat <strong>buy-and-hold in only 1 of 7</strong>, and that one is crypto daily, where
        buy-and-hold lost 68% a year. Being less bad than a collapsing altcoin is not an edge.
        <br /><br />
        The clearest single line is <strong>RWA at 4 hours: +119.9% a year gross, −16.7% net</strong>. At 471
        reversals a symbol-year, the cost eats a gross figure of that size and still leaves a loss. On the ten-year
        equity sample the rule is, before costs, almost exactly flat at +1.3% a year — costs take it to −9.1%.
      </GuideCallout>

      <P>
        Two things that will not rescue it, both measured: <strong>inverting every signal</strong> gives −31.5% a year
        on equities and −69.6% on crypto daily — when both directions lose, the problem is cost rather than sign — and
        <strong> adding a 2.5×ATR stop makes every single sample worse</strong>, because it cuts winners and adds fills
        without reducing how often the rule flips.
      </P>
      <P>
        The dials below do help, in the direction you would expect from the geometry rather than from the returns: on
        equities the buffer and the longer lookback each roughly halve the annual loss (−9.1% to −2.6%). None of them
        makes it positive.
      </P>

      <H id="clnbreak-dials">The dials, and what they actually do</H>
      <P>
        All of these live in <strong>Chart Settings → CLN Break</strong>, and every figure here is recomputed over 163
        crypto symbols by the verifier.
      </P>
      <Box sx={{ my: 2 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Dial</TableCell><TableCell>What it does</TableCell></TableRow></TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Buffer (× ATR)</strong></TableCell>
              <TableCell>The one that matters. 0.5×ATR takes bars breaching both levels from 13.4% down to 1.2% and cuts flips by 47%, because every ambiguous bar it removes is a reversal that no longer happens.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Bodies per level</strong></TableCell>
              <TableCell>Blunter version of the same thing: 10 bodies widen the channel to 2.07×ATR and flip once every 10.4 bars instead of every 4.3.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Regression length</strong> (Chart Settings → Candle — the chart&rsquo;s own CLN length)</TableCell>
              <TableCell>Not a CLN Break dial: the rule reads the length the chart draws, so the levels are always built from the bodies on screen. Goes the opposite way from how it reads. A <em>longer</em> regression makes each body <em>thinner</em>, so length 50 <em>narrows</em> the channel to 0.43×ATR. It still flips less often (every 5.9 bars) because the smoothed line holds one side of price for longer — but it does not widen anything.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Breach on close</strong></TableCell>
              <TableCell>Removes ambiguous bars entirely. The long level is always at or above the short level, so a single close can never be on both sides of them.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Stop (× ATR)</strong></TableCell>
              <TableCell>Ships at 0, because that is how the rule was measured. Switching one on makes the system no longer always-in; treat it as a risk choice, not a returns one.</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <H id="clnbreak-see">Seeing it on a chart</H>
      <P>
        Turn on the <strong>CLN Brk</strong> overlay for the two stepped level lines, and pick <strong>CLN_Break</strong>{' '}
        in the strategy selector for the ▲ B / ▼ S marks. Both are drawn from the same computation, so a line and a
        mark can never disagree about where the level was. Each level is drawn <strong>across the full width of the
        bar it applies to</strong>, so reading a wick against the line directly above or below it is reading it
        against the level that bar was tested against. The level for the bar you are standing in is drawn too —
        it is built from three already-closed bodies, so it is the one you could still place an order against.
      </P>
      <P>
        <OpenInApp to="/charts/BTCUSD?interval=1440&candle=CLN&overlays=clnBreakLevels&strategy=clnbreak" label="Open BTCUSD daily with CLN_Break on" />
      </P>
      <GuideCallout kind="info" title="8 hours needs a custom interval">
        480 minutes is not one of the built-in intervals, and Delta does not serve it natively either — the app builds
        an 8-hour bar from two 4-hour ones. Add it under <strong>Custom Intervals</strong> on the chart toolbar to get
        it in the picker.
      </GuideCallout>
    </>
  ),
};

export default Section_strategy_clnbreak;
